// ============================================================================
// CRM Lead sources — single source of truth.
//
// Leads arrive with a raw `source` string set by whoever captured them:
//   • the Coding Cloud landing form sends "landing" (backend default too)
//   • the Wayak / Nuvex landing forms send "wayak-landing" / "nuvex-landing"
//   • the Meta Lead Ads webhook sends "meta_ads"
//   • Google Ads imports send "google_ads"
//   • referrals are tagged "referido"
//
// These raw values are machine strings; the CRM shows human labels. This module
// maps any raw value to one of the canonical sources so the Fuentes view and the
// lead detail "Origen" use the exact same labels the landings are tagged with.
// ============================================================================

export interface SourceDef {
  /** Canonical key — stable id used for grouping. */
  key: string;
  /** Human label shown in the UI. */
  label: string;
  /** Category: Landing | Anuncios | Referido. */
  tipo: string;
  /** Remix icon class. */
  icon: string;
  /** Tailwind tone classes for the source chip/badge. */
  tone: string;
  /** Normalized raw `source` values that resolve to this source. */
  match: string[];
}

export const LEAD_SOURCES: SourceDef[] = [
  {
    key: "google_ads",
    label: "Google Ads",
    tipo: "Anuncios",
    icon: "ri-google-fill",
    tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    match: ["google_ads", "google-ads", "googleads", "google", "gads"],
  },
  {
    key: "meta_ads",
    label: "Meta Ads",
    tipo: "Anuncios",
    icon: "ri-meta-fill",
    tone: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    // "instagram" a secas es el canal de CHAT (mensajes directos), no un
    // anuncio: vive en la fuente `whatsapp` de abajo.
    match: [
      "meta_ads", "meta-ads", "metaads", "meta",
      "facebook", "facebook_ads", "fb", "fb_ads",
      "instagram_ads", "ig", "ig_ads",
    ],
  },
  {
    key: "referido",
    label: "Referidos",
    tipo: "Referido",
    icon: "ri-user-shared-2-line",
    tone: "bg-emerald-500/15 text-emerald-500",
    match: ["referido", "referidos", "referral", "referrals", "referencia"],
  },
  {
    key: "landing_coding_cloud",
    label: "Landing Coding Cloud",
    tipo: "Landing",
    icon: "ri-global-line",
    tone: "bg-primary-500/15 text-primary-500",
    match: [
      "landing", "coding-cloud-landing", "codingcloud-landing",
      "landing-coding-cloud", "coding-cloud", "codingcloud", "cc-landing", "landing-cc",
    ],
  },
  {
    key: "landing_wayak",
    label: "Landing Wayak",
    tipo: "Landing",
    icon: "ri-global-line",
    tone: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",
    match: ["wayak-landing", "wayak_landing", "landing-wayak", "wayak"],
  },
  {
    key: "landing_nuvex",
    label: "Landing Nuvex",
    tipo: "Landing",
    icon: "ri-global-line",
    tone: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
    match: ["nuvex-landing", "nuvex_landing", "landing-nuvex", "nuvex"],
  },
  // --- Movik (#1536): producto B2C que comparte plataforma con Wayak ---------
  {
    key: "landing_movik",
    label: "Landing Movik",
    tipo: "Landing",
    icon: "ri-global-line",
    tone: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
    match: ["movik-landing", "movik_landing", "landing-movik"],
  },
  {
    // Registro / formulario dentro de la app de Movik.
    key: "app_movik",
    label: "App Movik",
    tipo: "Registro",
    icon: "ri-smartphone-line",
    tone: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
    match: ["movik-app", "movik_app", "app-movik", "movik"],
  },
  // --- Funnel de captación WAYAK v2 (#1521) --------------------------------
  {
    // Formulario «Crear cuenta gratis» de la landing de WAYAK (#/prueba).
    key: "landing_wayak_prueba",
    label: "Landing Wayak — Prueba gratis",
    tipo: "Landing",
    icon: "ri-rocket-line",
    tone: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",
    match: ["wayak-landing-prueba", "wayak_landing_prueba", "landing-wayak-prueba"],
  },
  {
    // Solicitud de prueba piloto (landing de WAYAK → crmSignupIngest).
    key: "prueba_piloto",
    label: "Prueba piloto",
    tipo: "Landing",
    icon: "ri-flask-line",
    tone: "bg-teal-500/15 text-teal-600 dark:text-teal-400",
    match: ["prueba-piloto", "prueba_piloto", "pilot", "piloto", "pilot-request"],
  },
  {
    // Formulario del Programa de Partners.
    key: "partners",
    label: "Programa de Partners",
    tipo: "Landing",
    icon: "ri-hand-heart-line",
    tone: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400",
    match: ["wayak-landing-partners", "wayak_landing_partners", "partners", "partner", "socios"],
  },
  {
    // Reserva hecha directo en la agenda pública (sin formulario previo).
    key: "booking",
    label: "Agenda pública",
    tipo: "Landing",
    icon: "ri-calendar-check-line",
    tone: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
    match: ["landing-booking", "landing_booking", "booking", "agenda"],
  },
  {
    // Alta en la plataforma (registro público de WAYAK / NUVEX / CC-CONSOLE).
    key: "registro",
    label: "Registro en la plataforma",
    tipo: "Registro",
    icon: "ri-user-add-line",
    tone: "bg-indigo-500/15 text-indigo-500",
    match: ["registro", "signup", "sign-up", "register", "registration"],
  },
  {
    // Transportista dado de alta por Administración de Cargo Market (uno o por
    // lote, cargoCarrierInviteService): prospecto de telemetría.
    key: "cargo_market",
    label: "Cargo Market (alta de transportista)",
    tipo: "Consola",
    icon: "ri-truck-line",
    tone: "bg-indigo-500/15 text-indigo-500",
    match: ["cargo-market-transportista", "cargo_market_transportista", "cargo-market", "cargo_market"],
  },
  {
    // Conversaciones de la Bandeja de chat (WhatsApp, Messenger, Instagram DM).
    key: "whatsapp",
    label: "Chat (WhatsApp / Messenger / Instagram)",
    tipo: "Chat",
    icon: "ri-whatsapp-line",
    tone: "bg-green-500/15 text-green-600 dark:text-green-400",
    match: ["whatsapp", "messenger", "instagram", "chat", "wa"],
  },
  {
    key: "qr",
    label: "Código QR",
    tipo: "Físico",
    icon: "ri-qr-code-line",
    tone: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
    match: ["qr", "qr-code", "qr_code", "codigo-qr"],
  },
  {
    key: "pos",
    label: "Punto de venta",
    tipo: "Físico",
    icon: "ri-store-2-line",
    tone: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
    match: ["pos", "punto-de-venta", "punto_de_venta", "tienda"],
  },
  {
    key: "flyer",
    label: "Folletería",
    tipo: "Físico",
    icon: "ri-article-line",
    tone: "bg-lime-500/15 text-lime-700 dark:text-lime-400",
    match: ["flyer", "folleto", "folleteria", "print", "volante"],
  },
  {
    key: "app_store",
    label: "Tiendas de apps",
    tipo: "App",
    icon: "ri-smartphone-line",
    tone: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
    match: ["app_store", "app-store", "appstore", "play_store", "play-store", "playstore", "google-play"],
  },
  {
    // Creados a mano desde la consola, la API o el MCP.
    key: "manual",
    label: "Manual",
    tipo: "Consola",
    icon: "ri-edit-line",
    tone: "bg-gray-400/15 text-gray-500",
    match: ["manual", "console", "consola", "api", "mcp"],
  },
  {
    // Importación por Excel y campañas de llamadas salientes.
    key: "import",
    label: "Importación / campaña",
    tipo: "Consola",
    icon: "ri-file-excel-2-line",
    tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    match: ["import", "importacion", "excel", "campaign", "campana"],
  },
  {
    // Everything the HubSpot integration imports (contacts, companies, deals…)
    // arrives with source "hubspot"; a HubSpot form submission turned into a
    // lead arrives as "hubspot_form".
    key: "hubspot",
    label: "Importado de HubSpot",
    tipo: "Importado",
    icon: "ri-exchange-line",
    tone: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
    match: ["hubspot", "hubspot_form", "hubspot-form", "hubspotform"],
  },
];

/** Catch-all for raw sources that match no canonical source. */
export const OTHER_SOURCE: SourceDef = {
  key: "otro",
  label: "Otro",
  tipo: "Otro",
  icon: "ri-question-line",
  tone: "bg-gray-400/15 text-gray-500",
  match: [],
};

function normalize(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase().replace(/\s+/g, "-");
}

const BY_MATCH: Record<string, SourceDef> = (() => {
  const m: Record<string, SourceDef> = {};
  for (const s of LEAD_SOURCES) for (const raw of s.match) m[raw] = s;
  return m;
})();

/** Resolve a raw lead `source` string to its canonical source definition. */
export function resolveSource(raw: string | null | undefined): SourceDef {
  const n = normalize(raw);
  if (!n) return OTHER_SOURCE;
  return BY_MATCH[n] ?? OTHER_SOURCE;
}

/** Human label for a raw `source` string (falls back to the raw value). */
export function sourceLabel(raw: string | null | undefined): string {
  const n = normalize(raw);
  if (!n) return "—";
  return (BY_MATCH[n] ?? null)?.label ?? raw ?? "—";
}

// ===========================================================================
// Canales de captación (#1521). Es el `channel` de la atribución del lead —
// el contrato compartido con la landing y el registro de WAYAK — y se guarda
// también como `lead.channel`. A diferencia de `source` (quién capturó el
// lead), el canal dice por dónde LLEGÓ la persona (QR, folleto, anuncio…).
// ===========================================================================

export type LeadChannel =
  | "landing"
  | "whatsapp"
  | "meta_ads"
  | "qr"
  | "pos"
  | "flyer"
  | "app_store"
  | "referral"
  | "other";

export interface ChannelDef {
  key: LeadChannel;
  label: string;
  icon: string;
  tone: string;
}

export const CHANNELS: ChannelDef[] = [
  { key: "landing", label: "Sitio web", icon: "ri-global-line", tone: "bg-primary-500/15 text-primary-500" },
  { key: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line", tone: "bg-green-500/15 text-green-600 dark:text-green-400" },
  { key: "meta_ads", label: "Meta Ads", icon: "ri-meta-fill", tone: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  { key: "qr", label: "Código QR", icon: "ri-qr-code-line", tone: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
  { key: "pos", label: "Punto de venta", icon: "ri-store-2-line", tone: "bg-rose-500/15 text-rose-600 dark:text-rose-400" },
  { key: "flyer", label: "Folletería", icon: "ri-article-line", tone: "bg-lime-500/15 text-lime-700 dark:text-lime-400" },
  { key: "app_store", label: "Tiendas de apps", icon: "ri-smartphone-line", tone: "bg-violet-500/15 text-violet-600 dark:text-violet-400" },
  { key: "referral", label: "Referido", icon: "ri-user-shared-2-line", tone: "bg-emerald-500/15 text-emerald-500" },
  { key: "other", label: "Otro", icon: "ri-question-line", tone: "bg-gray-400/15 text-gray-500" },
];

const CHANNEL_BY_KEY: Record<string, ChannelDef> = Object.fromEntries(CHANNELS.map((c) => [c.key, c]));

/** Definición de un canal; cualquier valor desconocido o vacío es «Otro». */
export function resolveChannel(raw: string | null | undefined): ChannelDef {
  return CHANNEL_BY_KEY[normalize(raw)] ?? CHANNEL_BY_KEY.other;
}

/** Etiqueta legible de un canal. */
export function channelLabel(raw: string | null | undefined): string {
  return resolveChannel(raw).label;
}

/** Canal que corresponde a una fuente cuando el lead no trae atribución (espejo de `channelForSource` del backend). */
export function channelForSource(raw: string | null | undefined): LeadChannel {
  const n = normalize(raw);
  if (!n) return "other";
  if (["meta_ads", "meta-ads", "metaads", "facebook", "facebook_ads", "fb", "fb_ads", "instagram_ads", "ig_ads"].includes(n)) return "meta_ads";
  if (["whatsapp", "messenger", "instagram"].includes(n)) return "whatsapp";
  if (["referido", "referidos", "referral", "referrals", "referencia"].includes(n)) return "referral";
  if (["flyer", "folleto", "folleteria", "volante"].includes(n)) return "flyer";
  if (["qr", "qr-code", "qr_code", "codigo-qr"].includes(n)) return "qr";
  if (["pos", "punto-de-venta", "punto_de_venta"].includes(n)) return "pos";
  if (["app_store", "app-store", "appstore", "play_store", "play-store", "playstore"].includes(n)) return "app_store";
  if (n.includes("landing") || n === "prueba-piloto") return "landing";
  return "other";
}
